import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CategoryIcon from '@mui/icons-material/Category';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import {
  getAllProductsWithRelations,
  getRelatedProducts,
  updateProductRelations,
  ProductWithRelations,
  downloadProductRelationsTemplate,
  importProductRelations
} from '../services/productRelationService';
import { getProducts } from '../services/productService';
import ProductTypeManager from '../components/products/ProductTypeManager';
import {
  getAllProductsWithTypes,
  downloadProductTypesTemplate,
  importProductTypes
} from '../services/productTypeService';

const RelatedProductsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [products, setProducts] = useState<ProductWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [selectedRelated, setSelectedRelated] = useState<any[]>([]);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);
  
  // Pagination for Related Products
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Product Types states
  const [productTypesData, setProductTypesData] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [openTypeDialog, setOpenTypeDialog] = useState(false);
  const [selectedProductForType, setSelectedProductForType] = useState<any>(null);
  const [typesSearch, setTypesSearch] = useState('');
  
  // Pagination for Product Types
  const [typesPage, setTypesPage] = useState(0);
  const [typesRowsPerPage, setTypesRowsPerPage] = useState(10);
  const [totalTypes, setTotalTypes] = useState(0);

  // Import states
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importType, setImportType] = useState<'relations' | 'types'>('relations');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tabValue === 0) {
      loadProducts();
    } else {
      loadAllProductsForTypes();
    }
  }, [search, tabValue, page, rowsPerPage, typesSearch, typesPage, typesRowsPerPage]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await getAllProductsWithRelations(search, page + 1, rowsPerPage);
      
      setProducts(response.data || []);
      setTotalProducts(response.total || 0);
    } catch (error) {
      console.error('Error loading products:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء تحميل المنتجات' });
    } finally {
      setLoading(false);
    }
  };

  const loadAllProductsForTypes = async () => {
    try {
      setLoadingTypes(true);
      // جلب جميع المنتجات مع أنواعها من endpoint واحد
      const response = await getAllProductsWithTypes(typesSearch, typesPage + 1, typesRowsPerPage);
      
      setProductTypesData(response.data || []);
      setTotalTypes(response.total || 0);
    } catch (error) {
      console.error('Error loading products:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء تحميل المنتجات' });
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleManageRelations = async (product: ProductWithRelations) => {
    try {
      setSelectedProduct(product);
      
      // جلب المنتجات المرتبطة الحالية
      const relatedData = await getRelatedProducts(product._id);
      setRelatedProducts(relatedData.relatedProducts || []);
      setSelectedRelated(relatedData.relatedProducts || []);

      // جلب جميع المنتجات المتاحة
      const allProducts = await getProducts(1, -1);
      const available = allProducts.products.filter((p: any) => p._id !== product._id);
      setAvailableProducts(available);

      setOpenDialog(true);
    } catch (error) {
      console.error('Error loading related products:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء تحميل البيانات' });
    }
  };

  const handleSaveRelations = async () => {
    if (!selectedProduct) return;

    try {
      const relatedIds = selectedRelated.map(p => p._id);
      await updateProductRelations(selectedProduct._id, relatedIds);
      
      setAlert({ type: 'success', message: 'تم حفظ العلاقات بنجاح' });
      setOpenDialog(false);
      loadProducts();
    } catch (error: any) {
      console.error('Error saving relations:', error);
      setAlert({ 
        type: 'error', 
        message: error.response?.data?.message || 'حدث خطأ أثناء الحفظ' 
      });
    }
  };

  const handleManageType = (product: any) => {
    setSelectedProductForType(product);
    setOpenTypeDialog(true);
  };

  const handleTypeSuccess = () => {
    setAlert({ type: 'success', message: 'تم حفظ نوع المنتج بنجاح' });
    loadAllProductsForTypes();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeTypesPage = (event: unknown, newPage: number) => {
    setTypesPage(newPage);
  };

  const handleChangeTypesRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTypesRowsPerPage(parseInt(event.target.value, 10));
    setTypesPage(0);
  };

  // دوال الاستيراد والتحميل
  const handleDownloadTemplate = async (type: 'relations' | 'types') => {
    try {
      if (type === 'relations') {
        await downloadProductRelationsTemplate();
      } else {
        await downloadProductTypesTemplate();
      }
      setAlert({ type: 'success', message: 'تم تحميل القالب بنجاح' });
    } catch (error) {
      console.error('Error downloading template:', error);
      setAlert({ type: 'error', message: 'حدث خطأ أثناء تحميل القالب' });
    }
  };

  const handleOpenImportDialog = (type: 'relations' | 'types') => {
    setImportType(type);
    setSelectedFile(null);
    setImportResults(null);
    setOpenImportDialog(true);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // التحقق من نوع الملف
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        setAlert({ type: 'error', message: 'يرجى اختيار ملف Excel صالح (.xlsx أو .xls)' });
        return;
      }

      setSelectedFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setAlert({ type: 'error', message: 'يرجى اختيار ملف' });
      return;
    }

    try {
      setImporting(true);
      let response;

      if (importType === 'relations') {
        response = await importProductRelations(selectedFile);
      } else {
        response = await importProductTypes(selectedFile);
      }

      setImportResults(response.results);
      
      if (response.results.success > 0) {
        setAlert({
          type: 'success',
          message: `تم استيراد ${response.results.success} سجل بنجاح`
        });
        
        // إعادة تحميل البيانات
        if (importType === 'relations') {
          loadProducts();
        } else {
          loadAllProductsForTypes();
        }
      }

      if (response.results.failed > 0) {
        setAlert({
          type: 'warning',
          message: `تم استيراد ${response.results.success} سجل، فشل ${response.results.failed} سجل`
        });
      }
    } catch (error: any) {
      console.error('Error importing:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.message || 'حدث خطأ أثناء الاستيراد'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportDialog = () => {
    setOpenImportDialog(false);
    setSelectedFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        إدارة المنتجات
      </Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<LinkIcon />} label="المنتجات المرتبطة" />
        <Tab icon={<CategoryIcon />} label="أنواع المنتجات" />
      </Tabs>

      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {tabValue === 0 && (
        <>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="بحث عن منتج"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="اسم المنتج..."
            />
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadTemplate('relations')}
              color="primary"
            >
              تحميل قالب Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => handleOpenImportDialog('relations')}
              color="success"
            >
              استيراد من Excel
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>اسم المنتج</TableCell>
                    <TableCell>عدد الارتباطات</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product, index) => (
                    <TableRow key={product._id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.relatedCount}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleManageRelations(product)}
                        >
                          إدارة الارتباطات
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalProducts}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="عدد الصفوف في الصفحة:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
                }
              />
            </TableContainer>
          )}
        </>
      )}

      {tabValue === 1 && (
        <>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="بحث عن منتج"
              value={typesSearch}
              onChange={(e) => setTypesSearch(e.target.value)}
              placeholder="اسم المنتج..."
            />
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadTemplate('types')}
              color="primary"
            >
              تحميل قالب Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => handleOpenImportDialog('types')}
              color="success"
            >
              استيراد من Excel
            </Button>
          </Box>

          {loadingTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>اسم المنتج</TableCell>
                    <TableCell>نوع المنتج</TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productTypesData.map((item, index) => (
                    <TableRow key={item._id}>
                      <TableCell>{typesPage * typesRowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        {item.productType ? (
                          <Chip label={item.productType} color="primary" />
                        ) : (
                          <Chip label="غير محدد" color="default" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleManageType(item)}
                        >
                          {item.productType ? 'تعديل النوع' : 'تعيين نوع'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {productTypesData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        لا توجد منتجات
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalTypes}
                page={typesPage}
                onPageChange={handleChangeTypesPage}
                rowsPerPage={typesRowsPerPage}
                onRowsPerPageChange={handleChangeTypesRowsPerPage}
                labelRowsPerPage="عدد الصفوف في الصفحة:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} من ${count !== -1 ? count : `أكثر من ${to}`}`
                }
              />
            </TableContainer>
          )}
        </>
      )}

      {/* نافذة إدارة الارتباطات */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          إدارة المنتجات المرتبطة: {selectedProduct?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              options={availableProducts}
              getOptionLabel={(option) => option.name}
              value={selectedRelated}
              onChange={(event, newValue) => {
                setSelectedRelated(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="اختر المنتجات المرتبطة"
                  placeholder="ابحث عن منتج..."
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option._id}
                  />
                ))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>إلغاء</Button>
          <Button onClick={handleSaveRelations} variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {selectedProductForType && (
        <ProductTypeManager
          open={openTypeDialog}
          onClose={() => {
            setOpenTypeDialog(false);
            setSelectedProductForType(null);
          }}
          productId={selectedProductForType._id}
          productName={selectedProductForType.name}
          onSuccess={handleTypeSuccess}
        />
      )}

      {/* نافذة الاستيراد */}
      <Dialog
        open={openImportDialog}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          استيراد {importType === 'relations' ? 'المنتجات المرتبطة' : 'أنواع المنتجات'} من Excel
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="excel-file-input"
            />
            <label htmlFor="excel-file-input">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                startIcon={<UploadFileIcon />}
                disabled={importing}
              >
                {selectedFile ? selectedFile.name : 'اختر ملف Excel'}
              </Button>
            </label>

            {importing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  جاري الاستيراد...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {importResults && (
              <Box sx={{ mt: 3 }}>
                <Alert severity={importResults.failed === 0 ? 'success' : 'warning'}>
                  <Typography variant="subtitle2" gutterBottom>
                    نتائج الاستيراد:
                  </Typography>
                  <Typography variant="body2">
                    ✅ نجح: {importResults.success} سجل
                  </Typography>
                  <Typography variant="body2">
                    ❌ فشل: {importResults.failed} سجل
                  </Typography>
                </Alert>

                {importResults.errors && importResults.errors.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom color="error">
                      الأخطاء:
                    </Typography>
                    <Paper
                      sx={{
                        maxHeight: 200,
                        overflow: 'auto',
                        bgcolor: 'grey.50'
                      }}
                    >
                      <List dense>
                        {importResults.errors.slice(0, 10).map((error: any, index: number) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText
                                primary={`الصف ${error.row}`}
                                secondary={error.error}
                              />
                            </ListItem>
                            {index < Math.min(importResults.errors.length - 1, 9) && <Divider />}
                          </React.Fragment>
                        ))}
                        {importResults.errors.length > 10 && (
                          <ListItem>
                            <ListItemText
                              secondary={`... و ${importResults.errors.length - 10} خطأ آخر`}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  💡 نصائح:
                </Typography>
                <Typography variant="body2" component="div">
                  • قم بتحميل القالب أولاً لمعرفة التنسيق الصحيح
                  <br />
                  • تأكد من مطابقة أسماء المنتجات تماماً كما في النظام
                  <br />
                  • الحد الأقصى لحجم الملف: 10 ميجابايت
                  <br />
                  • يتم معالجة البيانات على دفعات لتجنب إرهاق الخادم
                </Typography>
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>
            إلغاء
          </Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!selectedFile || importing}
          >
            {importing ? 'جاري الاستيراد...' : 'استيراد'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelatedProductsPage;
